import React, { useState, useEffect, useCallback } from 'react';
import AdminService from '../../../services/AdminService';
import { ResolveModal } from './ResolveModal';
import { FixAuth0Modal } from '../FixAuth0Modal/FixAuth0Modal';
import './OrphanedAccountManagementTable.css';

/**
 * Shows list of orphaned accounts (not properly mapped with crm)
 * @returns {React.JSX.Element}
 */
export const OrphanedAccountManagementTable = () => {
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tracks per-row in-flight actions so we can disable buttons: keyed by row key.
  const [busyRow, setBusyRow] = useState(null);
  // The row whose Resolve modal is open (null when closed).
  const [resolveRow, setResolveRow] = useState(null);
  // Survivor CRM id entered by the admin for the re-sync-then-relink path.
  const [survivorCrmId, setSurvivorCrmId] = useState('');
  // Auth0/email outcome of the last relink ({ auth0, email_mismatch }), kept so
  // the modal can show it. Null until a relink lands or when it was all-clear.
  const [resolveOutcome, setResolveOutcome] = useState(null);
  // Session-only record of resolves that still need follow-up. A repaired row
  // drops out of the orphan report immediately, so without this list a warning
  // the admin clicked past is gone for good.
  const [resolutionNotes, setResolutionNotes] = useState([]);
  // User the Fix Auth0 modal is open for ({ userId, userEmail, oldEmail }).
  const [fixAuth0Target, setFixAuth0Target] = useState(null);

  const rowKey = (row) => `${row?.candidate_user_id ?? 'u'}:${row?.crm_id ?? 'c'}`;

  // Orphan reasons drive which repair actions apply:
  // - contact_unlinked: the contact already exists locally (has crm_id, no user_id)
  //   and the row supplies both candidate_user_id and crm_id, so relink is the clean
  //   fix.
  // - user_crm_id_unresolved: the user's crm_contact_id is a dead id from a merge, so
  //   the survivor contact must be re-synced into the DB first, then the user relinked
  //   to it (relink only updates an existing contacts row, it doesn't create one).
  const REASON_CONTACT_UNLINKED = 'contact_unlinked';
  const REASON_USER_CRM_ID_UNRESOLVED = 'user_crm_id_unresolved';

  const readableReasonMap = {
    [REASON_CONTACT_UNLINKED]: 'Contact unlinked',
    [REASON_USER_CRM_ID_UNRESOLVED]: 'CRM ID unresolved',
  }

  const loadOrphans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminService.getOrphanContacts();
      setOrphans(Array.isArray(data) ? data : (data?.rows ?? []));
    } catch (e) {
      setError(e.message || 'Failed to load orphaned accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrphans();
  }, [loadOrphans]);

  // Turns a non-quiet relink outcome into session notes for the follow-up list.
  const noteOutcome = (row, outcome) => {
    const notes = [];
    const status = outcome.auth0?.status;
    if (status === 'needs_manual') {
      notes.push(`${row.user_email}: relinked, but no Auth0 identity exists and they cannot log in or reset their password until it is repaired (Fix Auth).`);
    } else if (status === 'skipped' || status === 'error') {
      notes.push(`${row.user_email}: relinked, but the Auth0 check did not complete (${status}${outcome.auth0?.detail ? `: ${outcome.auth0.detail}` : ''}).`);
    }
    if (outcome.email_mismatch) {
      notes.push(`${row.user_email}: relinked. Still logs in as ${outcome.email_mismatch.login_email}; CRM now has ${outcome.email_mismatch.contact_email}. Changing a login email requires backend action.`);
    }
    if (notes.length) {
      setResolutionNotes((prev) => [...prev, ...notes]);
    }
  };

  const runAction = async (row, action) => {
    const key = rowKey(row);
    setBusyRow(key);
    setError(null);
    try {
      const result = await action();
      // Re-fetch so the repaired row drops out of the orphan list.
      await loadOrphans();
      const outcome = {
        auth0: result?.auth0 ?? null,
        email_mismatch: result?.email_mismatch ?? null,
      };
      const needsAttention =
        (outcome.auth0 && outcome.auth0.status !== 'already_linked') || outcome.email_mismatch;
      if (needsAttention) {
        // The row is gone from the orphan report the moment the relink lands,
        // so this modal is the only reliable place to act on what's left.
        setResolveOutcome(outcome);
        noteOutcome(row, outcome);
      } else {
        closeResolve();
      }
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setBusyRow(null);
    }
  };

  const openResolve = (row) => {
    setResolveRow(row);
    setSurvivorCrmId('');
    setResolveOutcome(null);
    setError(null);
  };

  const closeResolve = () => {
    setResolveRow(null);
    setSurvivorCrmId('');
    setResolveOutcome(null);
  };

  // Hands off from a relink outcome to the Fix Auth0 modal. When the outcome
  // carried an email mismatch the old login address is pre-filled as the
  // suspected location of the identity.
  const openFixAuth0FromOutcome = () => {
    setFixAuth0Target({
      userId: resolveRow.candidate_user_id,
      userEmail: resolveRow.user_email,
      oldEmail: resolveOutcome?.email_mismatch?.login_email || '',
    });
    closeResolve();
  };

  // Relink the user to a contact that already exists locally (contact_unlinked path).
  const handleRelinkExisting = (row) =>
    runAction(row, () => AdminService.relink(row.candidate_user_id, row.crm_id));

  // Re-sync the survivor contact into the DB, then relink the user to it
  // (user_crm_id_unresolved path). Order matters: relink only updates an existing
  // contacts row, so the survivor must be pulled in first. Relink runs last, so
  // its auth0/email_mismatch outcome covers both paths.
  const handleResyncAndRelink = (row, crmId) =>
    runAction(row, async () => {
      await AdminService.resyncContact(crmId);
      return AdminService.relink(row.candidate_user_id, crmId);
    });

  if (loading) {
    return <p>Loading orphaned accounts…</p>;
  }

  return (
    <div>
      {error && <p className="error" style={{ color: '#c0392b' }}>{error}</p>}
      {orphans.length === 0 ? (
        <p>No orphaned accounts found.</p>
      ) : (
        <table className="width-100 orphan-account-table">
          <thead>
            <tr>
              <th>User email</th>
              <th>User ID</th>
              <th>Contact email</th>
              <th>CRM ID</th>
              <th>Reason</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orphans.map((row) => {
              const key = rowKey(row);
              const isBusy = busyRow === key;
              const created = row.created_at ? new Date(row.created_at).toLocaleString() : '';
              // Leading indicator that this resolve will end in the
              // email-mismatch state: the CRM email moved but the login didn't.
              const emailChanged =
                row.reason === REASON_CONTACT_UNLINKED &&
                row.user_email && row.contact_email &&
                row.user_email.toLowerCase() !== row.contact_email.toLowerCase();
              return (
                <tr key={key}>
                  <td>{row.user_email}</td>
                  <td>{row.candidate_user_id}</td>
                  <td>
                    {row.contact_email}
                    {emailChanged && (
                      <span
                        className="orphan-badge-email-changed"
                        title="The CRM email differs from the user's login email, and after relinking, the login email will no longer match the CRM."
                      >
                        email changed in CRM
                      </span>
                    )}
                  </td>
                  <td>{row.crm_id}</td>
                  <td>{readableReasonMap[row.reason] ?? row.reason}</td>
                  <td>{created}</td>
                  <td>
                    <button
                      className="cta-button"
                      disabled={isBusy}
                      onClick={() => openResolve(row)}
                    >
                      Resolve
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {resolutionNotes.length > 0 && (
        <div className="orphan-resolution-notes">
          <h4>Needs follow-up (this session)</h4>
          <ul>
            {resolutionNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
          <p className="orphan-resolution-notes-hint">
            This list is not saved anywhere. Copy out anything that needs a ticket
            before leaving the page.
          </p>
        </div>
      )}
      {resolveRow && (
        <ResolveModal
          row={resolveRow}
          busy={busyRow === rowKey(resolveRow)}
          survivorCrmId={survivorCrmId}
          onSurvivorCrmIdChange={setSurvivorCrmId}
          reasonLabel={readableReasonMap[resolveRow.reason] ?? resolveRow.reason}
          recommendResync={resolveRow.reason === REASON_USER_CRM_ID_UNRESOLVED}
          recommendRelink={resolveRow.reason === REASON_CONTACT_UNLINKED}
          outcome={resolveOutcome}
          onFixAuth0={openFixAuth0FromOutcome}
          onRelinkExisting={() => handleRelinkExisting(resolveRow)}
          onResyncAndRelink={() => handleResyncAndRelink(resolveRow, survivorCrmId.trim())}
          onClose={closeResolve}
        />
      )}
      {fixAuth0Target && (
        <FixAuth0Modal
          userId={fixAuth0Target.userId}
          userEmail={fixAuth0Target.userEmail}
          initialOldEmail={fixAuth0Target.oldEmail}
          onClose={() => setFixAuth0Target(null)}
        />
      )}
    </div>
  );
};

export default OrphanedAccountManagementTable;
